import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import {
    segmentedControlButtonVariants,
    segmentedControlListVariants,
    segmentedControlTrackVariants,
    segmentedCountVariants,
    type SegmentedControlVariant,
} from './segmented-control-styles';

export interface SegmentedControlOption {
    value: string;
    label: ReactNode;
    count?: number;
    id?: string;
    disabled?: boolean;
}

interface SegmentedControlProps {
    options: SegmentedControlOption[];
    value: string;
    onValueChange: (value: string) => void;
    variant?: SegmentedControlVariant;
    ariaLabel?: string;
    id?: string;
    className?: string;
    trackClassName?: string;
    buttonClassName?: string;
}

export function SegmentedCountBadge({
    count,
    active,
    className,
}: {
    count: number;
    active: boolean;
    className?: string;
}) {
    return (
        <span
            data-slot="segmented-count"
            className={cn(segmentedCountVariants({ active }), className)}
        >
            {count}
        </span>
    );
}

export default function SegmentedControl({
    options,
    value,
    onValueChange,
    variant = 'pills',
    ariaLabel,
    id,
    className,
    trackClassName,
    buttonClassName,
}: SegmentedControlProps) {
    return (
        <div
            id={id}
            role="group"
            aria-label={ariaLabel}
            className={cn(segmentedControlListVariants({ variant }), className)}
        >
            <div className={cn(segmentedControlTrackVariants({ variant }), trackClassName)}>
                {options.map((option) => {
                    const isActive = value === option.value;

                    return (
                        <button
                            key={option.value}
                            id={option.id}
                            type="button"
                            aria-pressed={isActive}
                            disabled={option.disabled}
                            onClick={() => onValueChange(option.value)}
                            className={cn(
                                segmentedControlButtonVariants({
                                    variant,
                                    active: isActive,
                                }),
                                buttonClassName,
                            )}
                        >
                            <span className="inline-flex min-w-0 items-center justify-center">
                                {option.label}
                            </span>
                            {typeof option.count === 'number' && (
                                <SegmentedCountBadge count={option.count} active={isActive} />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
